import type { User } from '@/api/types/public';
import { toast } from '@/components/Toast';
import { clearGuestCheckoutCache } from '@/domain/payment/guest-checkout/browser-cache';
import { useStore } from '@/providers/app-store-provider';
import { getZeeloolSnowplowClient } from '@/reporting/integrations/snowplow-sdk/client';
import logger from '@/utils/client-logger';

/** 标准登录或注册成功回调接收的认证与用户数据。 */
interface LoginSuccessData {
  token: string;
  customer?: User;
  msg?: string;
}

/** 判断 SignUp 身份是否包含 C0、DEL 或 C1 控制字符。 */
function containsSignUpIdentityControlCharacter(value: string): boolean {
  return Array.from(value).some((character) => {
    const codePoint = character.charCodeAt(0);

    // C0 为 0-31，DEL/C1 为 127-159。
    return codePoint <= 31 || (codePoint >= 127 && codePoint <= 159);
  });
}

/** 把注册身份规范化为非空、非字符串 nullish 的值。 */
function normalizeSignUpIdentityValue(value: unknown): string | null {
  // 真正的 nullish 值没有可追踪身份。
  if (value === null || value === undefined) return null;

  const rawValue = String(value);

  // 必须在 trim 前拒绝首尾控制字符，不能把非法身份清洗成可发送值。
  if (containsSignUpIdentityControlCharacter(rawValue)) return null;

  const normalizedValue = rawValue.trim();
  const lowerCaseValue = normalizedValue.toLowerCase();

  // 空字符串和大小写任意的字符串 nullish 值都应拒绝。
  if (!normalizedValue || lowerCaseValue === 'null' || lowerCaseValue === 'undefined') return null;

  return normalizedValue;
}

/** 非阻塞调用 SDK 专用 SignUp，并消费同步与异步异常。 */
function trackSignUpSafely(customer: LoginSuccessData['customer']): void {
  const uuid = normalizeSignUpIdentityValue(customer?.uuid);
  const customerId = normalizeSignUpIdentityValue(customer?.id);

  // 两个规范化身份必须同时存在，不能补猜任一字段。
  if (!uuid || !customerId) return;

  const client = getZeeloolSnowplowClient();

  // singleton 尚未配置时登录主链路继续执行。
  if (!client) return;

  try {
    void client.trackSignUp({ uuid, customerId }).catch(() => {
      // Promise rejection 只记录稳定类别，不泄漏身份或底层异常。
      logger.error('[useLoginSuccess] signup_track_failed');
    });
  } catch {
    // SDK 同步异常同样不能阻断缓存、toast 与登录结果。
    logger.error('[useLoginSuccess] signup_track_failed');
  }
}

/** 管理标准登录或注册成功后的用户状态、缓存与非阻塞上报。 */
export default function useLoginSuccess() {
  const setToken = useStore((state) => state.setToken);
  const setUserInfo = useStore((state) => state.setUserInfo);
  const setWishNum = useStore((state) => state.setWishNum);
  const setCart = useStore((state) => state.setCart);
  const resolveCaLensFlowExperiment = useStore((state) => state.resolveCaLensFlowExperiment);

  /** 在认证成功后同步用户状态、清理游客缓存并发送 Snowplow SignUp。 */
  const handleLoginSuccess = async (data: LoginSuccessData) => {
    try {
      // 设置token和用户信息到store (localStorage存储会在setToken和setUserInfo方法内部处理)
      setToken(data.token);
      if (data.customer) {
        setUserInfo(data.customer);
        setWishNum(data.customer.wish_num);
      }
      // 登录后由后端按账号身份重新返回 A/B 分组；不复用游客态结果。
      void resolveCaLensFlowExperiment({ force: true });
      if (data.customer) {
        setCart();
      }

      // 状态与购物车同步完成后、缓存和 toast 之前发送专用 SignUp。
      trackSignUpSafely(data.customer);

      clearGuestCheckoutCache();

      // 显示成功消息
      if (data.msg) {
        toast.success(data.msg);
      }
    } catch (error) {
      logger.error('Failed to handle login success:', error);
      throw error;
    }
  };

  return { handleLoginSuccess };
}
